import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getApiBaseUrl } from '../config/api';
import useLiveRefresh from '../hooks/useLiveRefresh';
import './GroupsFeedSidebar.css';

const FALLBACK_PHOTO =
  'https://fms.techtreeglobal.com/assets/uploads/1743233100_key.png';

function buildPhotoUrl(photo, apiUrl) {
  if (!photo) return FALLBACK_PHOTO;
  if (/^https?:\/\//i.test(photo)) return photo;
  const base = String(apiUrl || '').replace(/\/+$/, '');
  if (photo.startsWith('/')) return `${base}${photo}`;
  if (photo.includes('uploads/')) return `${base}/${photo}`;
  return `${base}/uploads/groups/${photo}`;
}

export default function GroupsFeedSidebar({ user }) {
  const API_URL = getApiBaseUrl();
  const userId = user?.id;
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (options = {}) => {
    const silent = options.silent === true;
    if (!userId) {
      if (!silent) setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API_URL}/get_groups.php?user_id=${userId}`);
      const data = await res.json();
      if (data.success) {
        setGroups(data.my_groups || []);
      }
    } catch (err) {
      console.error('Groups sidebar load error:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [API_URL, userId]);

  useEffect(() => {
    load();
  }, [load]);

  useLiveRefresh(() => load({ silent: true }), 20000, Boolean(userId));

  const preview = groups.slice(0, 1);
  const hasMore = groups.length > 1;

  return (
    <aside className="gfs-panel">
      <div className="gfs-header">
        <h2 className="gfs-title">My Groups</h2>
        <Link to="/groups/groupinpage" className="gfs-see-more">
          See more
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </Link>
      </div>

      <div className="gfs-list">
        {loading ? (
          <p className="gfs-empty">Loading…</p>
        ) : preview.length === 0 ? (
          <div className="gfs-empty-box">
            <p className="gfs-empty">You have not joined any groups yet.</p>
            <Link to="/groups/groupinpage" className="gfs-cta">
              Browse groups
            </Link>
          </div>
        ) : (
          <>
            {preview.map((g) => (
              <Link key={g.id} to={`/group/${g.id}`} className="gfs-item">
                <img
                  src={buildPhotoUrl(g.group_photos, API_URL)}
                  alt=""
                  className="gfs-avatar"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_PHOTO;
                  }}
                />
                <div className="gfs-info">
                  <span className="gfs-name">{g.name || 'Untitled Group'}</span>
                  {g.visibility && (
                    <span className="gfs-meta">{g.visibility}</span>
                  )}
                </div>
              </Link>
            ))}
            {hasMore && (
              <Link to="/groups/groupinpage" className="gfs-footer-link">
                See all groups
              </Link>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
